import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AutoComplete, Space, Spin, Typography } from 'antd';
import {
  IdcardOutlined,
  MailOutlined,
  PhoneOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { debounce } from 'lodash';
import axios from '../services/axiosInstance';

const { Text } = Typography;

const getCustomerName = (customer) =>
  `${customer?.first_name || ''} ${customer?.last_name || ''}`.trim();

/** Unique, human-readable option value (shown on hover preview). */
const getOptionValue = (customer) => {
  const name = getCustomerName(customer) || `Customer #${customer.person_id}`;
  if (customer.email) {
    return `${name} (${customer.email})`;
  }
  return `${name} (#${customer.person_id})`;
};

const toOption = (customer) => {
  const name = getCustomerName(customer) || `Customer #${customer.person_id}`;
  return {
    value: getOptionValue(customer),
    customer,
    label: (
      <div>
        <div>
          <Text strong>{name}</Text>
        </div>
        {customer.email && (
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {customer.email}
            </Text>
          </div>
        )}
        {customer.phone && (
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {customer.phone}
            </Text>
          </div>
        )}
      </div>
    ),
  };
};

const CustomerAutocomplete = ({
  selectedCustomerId,
  onSelect,
  customers = [],
  placeholder = 'Search customers...',
  disabled = false,
}) => {
  const [internalValue, setInternalValue] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const selectingRef = useRef(false);
  const optionsRef = useRef([]);

  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  // Sync display when selectedCustomerId changes (form load, new customer, clear)
  useEffect(() => {
    if (!selectedCustomerId) {
      setSelectedCustomer(null);
      setInternalValue('');
      return;
    }

    const localMatch = customers.find(
      (c) => String(c.person_id) === String(selectedCustomerId)
    );

    if (localMatch) {
      setSelectedCustomer(localMatch);
      setInternalValue(getCustomerName(localMatch));
      return;
    }

    let cancelled = false;
    axios
      .get(`/persons/${selectedCustomerId}`)
      .then((response) => {
        if (cancelled) return;
        const fetchedCustomer = response.data;
        if (fetchedCustomer) {
          setSelectedCustomer(fetchedCustomer);
          setInternalValue(getCustomerName(fetchedCustomer));
        }
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Error fetching customer:', error);
        setSelectedCustomer(null);
        setInternalValue('');
      });

    return () => {
      cancelled = true;
    };
  }, [selectedCustomerId, customers]);

  const runSearch = useMemo(
    () =>
      debounce(async (query) => {
        if (!query || query.trim().length < 2) {
          setOptions([]);
          setLoading(false);
          return;
        }

        setLoading(true);
        try {
          const response = await axios.get('/persons', {
            params: {
              search: query,
              limit: 20,
            },
          });

          const allPersons = response.data?.persons || [];
          const results = allPersons.filter(
            (person) => person.person_type?.toLowerCase() === 'customer'
          );
          setOptions(results.map(toOption));
        } catch (error) {
          console.error('Error searching customers:', error);
          if (customers && customers.length > 0) {
            const queryLower = query.toLowerCase();
            const results = customers.filter((customer) => {
              const fullName = getCustomerName(customer).toLowerCase();
              const email = (customer.email || '').toLowerCase();
              return (
                fullName.includes(queryLower) || email.includes(queryLower)
              );
            });
            setOptions(results.map(toOption));
          } else {
            setOptions([]);
          }
        } finally {
          setLoading(false);
        }
      }, 300),
    [customers]
  );

  useEffect(() => () => runSearch.cancel(), [runSearch]);

  const handleChange = (value) => {
    if (selectingRef.current) return;

    const nextValue = value ?? '';
    // Keep controlled value in sync with hover preview / typing so focus is not stolen
    setInternalValue(nextValue);

    const matchedOption = optionsRef.current.find((o) => o.value === nextValue);
    if (matchedOption) {
      // Hover/keyboard highlight preview — do not clear selection or re-search
      return;
    }

    if (
      selectedCustomer &&
      nextValue !== getCustomerName(selectedCustomer)
    ) {
      setSelectedCustomer(null);
      if (onSelect) {
        onSelect(null);
      }
    }

    if (nextValue.trim().length >= 2) {
      runSearch(nextValue);
    } else {
      setOptions([]);
    }
  };

  const handleSelect = (_value, option) => {
    const customer = option?.customer;
    if (!customer) return;

    selectingRef.current = true;
    setSelectedCustomer(customer);
    setInternalValue(getCustomerName(customer));
    setOptions([]);
    if (onSelect) {
      onSelect(customer);
    }
    window.setTimeout(() => {
      selectingRef.current = false;
    }, 0);
  };

  const handleClear = () => {
    setInternalValue('');
    setSelectedCustomer(null);
    setOptions([]);
    if (onSelect) {
      onSelect(null);
    }
  };

  return (
    <div data-testid="customer-autocomplete">
      <AutoComplete
        value={internalValue}
        options={options}
        onChange={handleChange}
        onSelect={handleSelect}
        onClear={handleClear}
        disabled={disabled}
        allowClear
        defaultActiveFirstOption={false}
        placeholder={placeholder}
        style={{ width: '100%' }}
        notFoundContent={
          loading ? (
            <Spin size="small" />
          ) : internalValue.trim().length >= 2 ? (
            'No customers found'
          ) : (
            'Type at least 2 characters'
          )
        }
      />

      {selectedCustomer && (
        <div
          data-testid="selected-customer-info"
          style={{
            marginTop: 8,
            padding: '8px 12px',
            background: '#fafafa',
            border: '1px solid #f0f0f0',
            borderRadius: 6,
          }}
        >
          <Text
            type="secondary"
            style={{ fontSize: 11, display: 'block', marginBottom: 4 }}
          >
            Selected customer
          </Text>
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            <Space size={6}>
              <UserOutlined style={{ color: '#8c8c8c' }} />
              <Text strong style={{ fontSize: 13 }}>
                {getCustomerName(selectedCustomer) || '—'}
              </Text>
            </Space>
            <Space size={6} wrap>
              <IdcardOutlined style={{ color: '#8c8c8c' }} />
              <Text type="secondary" style={{ fontSize: 12 }}>
                ID {selectedCustomer.person_id}
              </Text>
              {selectedCustomer.email && (
                <>
                  <MailOutlined style={{ color: '#8c8c8c', marginLeft: 4 }} />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {selectedCustomer.email}
                  </Text>
                </>
              )}
              {selectedCustomer.phone && (
                <>
                  <PhoneOutlined style={{ color: '#8c8c8c', marginLeft: 4 }} />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {selectedCustomer.phone}
                  </Text>
                </>
              )}
            </Space>
          </Space>
        </div>
      )}
    </div>
  );
};

export default CustomerAutocomplete;
